/* eslint-disable @typescript-eslint/no-explicit-any -- Leaflet nạp từ CDN, không có type declarations */
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, MapPin, Search } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { getMatchMeta, type LocationValue, type ProvinceMeta } from "@/lib/boide-api";

const LEAFLET_VERSION = "1.9.4";
const LEAFLET_CSS = `https://unpkg.com/leaflet@${LEAFLET_VERSION}/dist/leaflet.css`;
const LEAFLET_JS = `https://unpkg.com/leaflet@${LEAFLET_VERSION}/dist/leaflet.js`;
const ICON_BASE = `https://unpkg.com/leaflet@${LEAFLET_VERSION}/dist/images`;
const VN_CENTER: [number, number] = [16.2, 107.8]; // tâm Việt Nam

type NominatimResult = { lat: string; lon: string; display_name: string };

// Bỏ dấu + hạ chữ thường để khớp tên tỉnh trong chuỗi địa chỉ.
function stripAccents(s: string): string {
  return s
    .replace(/đ/g, "d")
    .replace(/Đ/g, "D")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

/** Load Leaflet (JS + CSS) từ CDN 1 lần, trả về window.L khi sẵn sàng. */
function useLeaflet(): any | null {
  const [L, setL] = useState<any | null>(
    typeof window !== "undefined" ? ((window as any).L ?? null) : null,
  );
  useEffect(() => {
    if (typeof window === "undefined" || (window as any).L) {
      if ((window as any).L) setL((window as any).L);
      return;
    }
    if (!document.querySelector(`link[data-leaflet]`)) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = LEAFLET_CSS;
      link.setAttribute("data-leaflet", "1");
      document.head.appendChild(link);
    }
    let script = document.querySelector<HTMLScriptElement>(`script[data-leaflet]`);
    const onReady = () => setL((window as any).L ?? null);
    if (!script) {
      script = document.createElement("script");
      script.src = LEAFLET_JS;
      script.async = true;
      script.setAttribute("data-leaflet", "1");
      script.addEventListener("load", onReady);
      document.body.appendChild(script);
    } else {
      script.addEventListener("load", onReady);
    }
    return () => script?.removeEventListener("load", onReady);
  }, []);
  return L;
}

/** Khớp 1 chuỗi địa chỉ → mã tỉnh (theo label + alias cũ). */
function matchProvince(text: string, provinces: ProvinceMeta[]): ProvinceMeta | null {
  const key = stripAccents(text);
  for (const p of provinces) {
    const names = [p.label, ...(p.aliases || [])].map(stripAccents);
    if (names.some((n) => n && key.includes(n))) return p;
  }
  return null;
}

export function LocationPicker({
  value,
  onChange,
}: {
  value: LocationValue;
  onChange: (v: LocationValue) => void;
}) {
  const L = useLeaflet();
  const meta = useQuery({
    queryKey: ["mm-meta"],
    queryFn: getMatchMeta,
    staleTime: 60 * 60 * 1000,
  });
  const provinces = meta.data?.provinces ?? [];

  const mapEl = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<any>(null);
  const markerRef = useRef<any>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const valueRef = useRef(value);
  valueRef.current = value;

  const [query, setQuery] = useState("");
  const [results, setResults] = useState<NominatimResult[]>([]);
  const [searching, setSearching] = useState(false);

  const icon = useMemo(() => {
    if (!L) return null;
    return L.icon({
      iconUrl: `${ICON_BASE}/marker-icon.png`,
      iconRetinaUrl: `${ICON_BASE}/marker-icon-2x.png`,
      shadowUrl: `${ICON_BASE}/marker-shadow.png`,
      iconSize: [25, 41],
      iconAnchor: [12, 41],
    });
  }, [L]);

  // Cập nhật toạ độ (từ pin/search) + tự suy tỉnh nếu có nhãn địa chỉ.
  const applyCoords = (lat: number, lon: number, displayName?: string) => {
    const next: LocationValue = {
      ...valueRef.current,
      latitude: Number(lat.toFixed(3)),
      longitude: Number(lon.toFixed(3)),
    };
    if (displayName) {
      next.location_label = displayName.split(",").slice(0, 2).join(",").trim();
      const hit = matchProvince(displayName, provinces);
      if (hit) {
        next.province_code = hit.code;
        next.region = hit.region;
      }
    }
    onChangeRef.current(next);
  };

  // Khởi tạo map khi Leaflet sẵn sàng.
  useEffect(() => {
    if (!L || !icon || !mapEl.current || mapRef.current) return;
    const start: [number, number] =
      value.latitude != null && value.longitude != null
        ? [value.latitude, value.longitude]
        : VN_CENTER;
    const map = L.map(mapEl.current, { attributionControl: false }).setView(
      start,
      value.latitude != null ? 13 : 5,
    );
    L.tileLayer(`https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png`, {
      maxZoom: 19,
    }).addTo(map);
    const marker = L.marker(start, { draggable: true, icon }).addTo(map);
    marker.on("dragend", () => {
      const p = marker.getLatLng();
      applyCoords(p.lat, p.lng);
      reverseGeocode(p.lat, p.lng);
    });
    map.on("click", (e: any) => {
      marker.setLatLng(e.latlng);
      applyCoords(e.latlng.lat, e.latlng.lng);
      reverseGeocode(e.latlng.lat, e.latlng.lng);
    });
    mapRef.current = map;
    markerRef.current = marker;
    // Map cần invalidateSize sau khi container hiện (mobile/desktop).
    setTimeout(() => map.invalidateSize(), 200);
    return () => {
      map.remove();
      mapRef.current = null;
      markerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [L, icon]);

  // Reverse geocode (best-effort) để điền nhãn + tỉnh khi thả ghim.
  const reverseGeocode = async (lat: number, lon: number) => {
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&accept-language=vi&lat=${lat}&lon=${lon}`,
      );
      const j = await res.json();
      if (j?.display_name) applyCoords(lat, lon, j.display_name);
    } catch {
      /* im lặng — toạ độ vẫn dùng được dù không có nhãn */
    }
  };

  const runSearch = async () => {
    const q = query.trim();
    if (q.length < 3) return;
    setSearching(true);
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&countrycodes=vn&accept-language=vi&limit=5&q=${encodeURIComponent(q)}`,
      );
      setResults((await res.json()) as NominatimResult[]);
    } catch {
      setResults([]);
    } finally {
      setSearching(false);
    }
  };

  const pickResult = (r: NominatimResult) => {
    const lat = parseFloat(r.lat);
    const lon = parseFloat(r.lon);
    applyCoords(lat, lon, r.display_name);
    setResults([]);
    setQuery(r.display_name.split(",").slice(0, 2).join(",").trim());
    if (mapRef.current && markerRef.current) {
      markerRef.current.setLatLng([lat, lon]);
      mapRef.current.setView([lat, lon], 14);
    }
  };

  const onProvince = (code: string) => {
    const p = provinces.find((x) => x.code === code);
    onChange({
      ...value,
      province_code: code || null,
      region: p?.region ?? null,
      location_label: value.location_label || p?.label || null,
    });
  };

  return (
    <div className="space-y-3">
      {/* Tỉnh/thành (sau sáp nhập) — bắt buộc nhẹ để chấm vùng miền */}
      <div>
        <Label className="mb-1.5 block text-sm">Tỉnh/thành (sau sáp nhập 2025)</Label>
        <select
          value={value.province_code ?? ""}
          onChange={(e) => onProvince(e.target.value)}
          className="h-10 w-full rounded-md border border-border/50 bg-background/60 px-3 text-sm outline-none focus:border-primary/60"
        >
          <option value="">— Chọn tỉnh/thành —</option>
          {provinces.map((p) => (
            <option key={p.code} value={p.code}>
              {p.label}
              {p.aliases?.length ? ` (gồm ${p.aliases.join(", ")})` : ""}
            </option>
          ))}
        </select>
      </div>

      {/* Ô tìm địa chỉ (OpenStreetMap, miễn phí) */}
      <div>
        <Label className="mb-1.5 block text-sm">Tìm địa chỉ để ghim chính xác</Label>
        <div className="flex gap-2">
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), runSearch())}
            placeholder="VD: Quận 1, TP.HCM"
          />
          <Button type="button" variant="outline" onClick={runSearch} disabled={searching}>
            {searching ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Search className="h-4 w-4" />
            )}
          </Button>
        </div>
        {results.length > 0 ? (
          <div className="mt-1 overflow-hidden rounded-md border border-border/50 bg-background/80">
            {results.map((r, i) => (
              <button
                key={i}
                type="button"
                onClick={() => pickResult(r)}
                className="block w-full truncate px-3 py-2 text-left text-xs hover:bg-primary/10"
              >
                {r.display_name}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      {/* Bản đồ thả ghim — responsive: thấp hơn trên mobile */}
      <div className="overflow-hidden rounded-xl border border-border/50">
        <div ref={mapEl} className="h-52 w-full sm:h-72" />
      </div>
      <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
        <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
        {value.latitude != null
          ? `Đã ghim: ${value.location_label || `${value.latitude}, ${value.longitude}`}. Kéo ghim hoặc chạm bản đồ để chỉnh.`
          : "Chạm vào bản đồ hoặc tìm địa chỉ để thả ghim vị trí. Người khác chỉ thấy khoảng cách ước lượng, không thấy toạ độ."}
      </p>

      {/* Note địa chỉ cũ (nếu quen tên trước sáp nhập) */}
      <div>
        <Label className="mb-1.5 block text-sm">
          Địa chỉ cũ (tuỳ chọn — nếu quen tên trước sáp nhập)
        </Label>
        <Input
          value={value.legacy_location ?? ""}
          onChange={(e) => onChange({ ...value, legacy_location: e.target.value || null })}
          placeholder="VD: TP. Thủ Đức cũ, Q.2…"
          maxLength={80}
        />
      </div>
    </div>
  );
}
